import { defineComponent as _defineComponent } from 'vue'
import { reactive, inject, toRefs } from "vue";
import TalkRoomController from "@/controllers/communityTalk/talkRoomController";
import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";
import { ApiPostLoadStatus, ApiPostLoadStatusType } from "@/modules/types/apiLoadStatusType";
import LoginUtil from "@/modules/utils/loginUtil";
import PostPermissionUtil from "@/modules/utils/postPermissionUtil";
import ApiPostLoadMaster from "@/components/common/api/ApiPostLoadMaster.vue";
import RequireLogin from "@/components/communityTalk/RequireLogin.vue";
import PostComplete from "@/components/communityTalk/PostComplete.vue";
import TalkRoomPostNoticeMail from "@/components/communityTalk/talkRoom/TalkRoomPostNoticeMail.vue";
import AppConst from "@/modules/const";
import CustomTextarea from "@/components/common/form/CustomTextarea.vue";
import CustomLabel from "@/components/communityTalk/CustomLabel.vue";
import { ApiErrorViewModel, ApiValidationErrorViewModel } from "@/viewModels/apiErrorViewModel";
import PostNickName from "@/components/communityTalk/PostNickName.vue";
import PostMedia from "@/components/communityTalk/PostMedia.vue";
import PostProfilePicture from "@/components/communityTalk/PostProfilePicture.vue";
import PostReviewerAttribute from "@/components/communityTalk/PostReviewerAttribute.vue";
import { PostMediaViewModel } from "@/viewModels/communityTalk/postMediaViewModel";
import { LayoutTagGetResponse_WordSetting } from "@/apiRequests/models/communityTalk/layoutTagGetResponse";

interface State {
	/**
	 * トークルーム投稿内容。
	 */
	contents: string;
	/**
	 * トークルーム投稿内容。入力値が最大文字長を超えているかどうか。
	 */
	isOverContentsMaxLength: boolean;
	/**
	 * トークルーム投稿時のAPIステータス。
	 */
	status: ApiPostLoadStatusType;
	/**
	 * APIバリデーションエラーオブジェクト。
	 */
	apiValidationErrorViewModel: ApiValidationErrorViewModel | null;
	/**
	 * 作成されたトークルームID。
	 */
	postTalkRoomId: string;
	/**
	 * フォーム多重送信制御用フラグ
	 */
	isProcessing: boolean;
	/**
	 * 画像・動画を投稿中かどうか。
	 */
	isMediaPostProcessing: boolean;
	/**
	 * 内容が未入力かどうか。
	 */
	isContentsEmpty: boolean;
	/**
	 * ニックネームが未入力かどうか。
	 */
	isNickNameEmpty: boolean;
}
// reactiveな変数宣言
interface Props {
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: string;
	/**
	 * 表示タグ設定>文言設定。
	 */
	wordSettings: LayoutTagGetResponse_WordSetting;
}

export default /*#__PURE__*/_defineComponent({
  props: {
    communityTalkStoreKey: { type: String, required: true },
    wordSettings: { type: null, required: true }
  },
  setup(__props: any, { expose }) {
  expose()

const props = __props as {
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: string;
	/**
	 * 表示タグ設定>文言設定。
	 */
	wordSettings: LayoutTagGetResponse_WordSetting;
}
/**
 * トークルーム投稿。
 */

const state = reactive<State>({
	contents: "",
	isOverContentsMaxLength: false,
	status: ApiPostLoadStatus.beforeRequest,
	apiValidationErrorViewModel: null,
	postTalkRoomId: "",
	isProcessing: false,
	isMediaPostProcessing: false,
	isContentsEmpty: false,
	isNickNameEmpty: false,
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-undef
const { contents, isOverContentsMaxLength, status, apiValidationErrorViewModel, postTalkRoomId, isContentsEmpty, isNickNameEmpty } = toRefs(state);



// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;

const isPostable = PostPermissionUtil.isPostable(
	communityTalkStore.getGeneralSettings().talkRoomPostPermissionSetting,
	communityTalkStore.getGeneralSettings().talkRoomPostPermissionSpecificECMemberIds,
	communityTalkStore.getGeneralSettings().nonMemberIdList,
	communityTalkStore.getSiteUserId()
);
const isFormVisible = PostPermissionUtil.isFormVisible(
	communityTalkStore.getGeneralSettings().talkRoomPostPermissionSetting,
	communityTalkStore.getGeneralSettings().talkRoomPostPermissionSpecificECMemberIds,
	communityTalkStore.getSiteUserId()
);

// ニックネームが必須かどうか。
const isRequiredNickName = communityTalkStore.getGeneralSettings().nicknameRequiredSetting === "Required";

// トークルーム投稿内容入力欄変更時、閾値を超えているかどうかを判定
const onChangeContents = (): void => {
	if (state.contents.length > AppConst.TALKROOM_CONTENTS_MAX_LENGTH) {
		state.isOverContentsMaxLength = true;
	} else {
		state.isOverContentsMaxLength = false;
	}
};

// メディア投稿完了時の処理
const onSucessUploadMedias = (viewModels: PostMediaViewModel[]): void => {
	communityTalkStore.setTalkRoomPostMedia(viewModels);
};

// メディア投稿リセット時の処理
const onResetMedias = (): void => {
	communityTalkStore.resetTalkRoomPostMedia();
};

const talkRoomController = new TalkRoomController();
/**
 * トークルーム投稿処理。
 */
const post = async (): Promise<void> => {
	state.isProcessing = true;
	state.isNickNameEmpty = false;
	state.isContentsEmpty = false;

	// 内容が未入力の場合はエラーメッセージを表示
	if (state.contents === "") {
		state.isContentsEmpty = true;
		state.isProcessing = false;
	}
	// 内容が閾値を超えている場合何もしない
	if (state.isOverContentsMaxLength) {
		state.isProcessing = false;
	}
	// ニックネーム入力が必須かつ未入力の場合はエラーメッセージを表示
	if (isRequiredNickName && (communityTalkStore.getCommunityTalkUserNickName() == null || communityTalkStore.getCommunityTalkUserNickName() === "")) {
		state.isNickNameEmpty = true;
		state.isProcessing = false;
	}

	if (!state.isProcessing) return;

	state.status = ApiPostLoadStatus.loading;
	try {
		// SiteUserIdの送信可否を確認
		// 不可である場合、undefindを送信する。
		let siteUserId = communityTalkStore.getSiteUserId();
		if (!LoginUtil.canSendSiteUserId(siteUserId, communityTalkStore.getGeneralSettings().nonMemberIdList, communityTalkStore.getGeneralSettings().talkRoomPostPermissionSetting)) {
			siteUserId = undefined;
		}

		const talkRoomPostResponse = await talkRoomController.postTalkRoomData(
			communityTalkStore.getCommunityTalkUserNickName(),
			communityTalkStore.getCommunityTalkUserReviewerAttributes(),
			communityTalkStore.getCommunityTalkUserProfilePicViewModel().profilePicIdTmp,
			communityTalkStore.getTalkRoomPostMedia(),
			communityTalkStore.getTenantId(),
			communityTalkStore.getTagCd(),
			communityTalkStore.getSubjectCd(),
			communityTalkStore.getSubjectName(),
			communityTalkStore.getTalkRoomUrl(),
			state.contents,
			communityTalkStore.getReviCoCookieId(),
			siteUserId
		);
		state.postTalkRoomId = talkRoomPostResponse.talkRoomId;
		state.status = ApiPostLoadStatus.success;
		// LocalStorageにプリセット情報を保持
		communityTalkStore.setCommunityTalkViewModelToLocalStorage();
	} catch (e) {
		// バリデーションエラーであればエラーを表示させ、もう1度入力できるようにする
		if ((e as ApiErrorViewModel).isValidationError) {
			state.apiValidationErrorViewModel = (e as ApiErrorViewModel).validationErrorContents;
			state.status = ApiPostLoadStatus.beforeRequest;
		} else {
			// そうでなければエラー扱い(入力はもう一度出来ない)
			state.status = ApiPostLoadStatus.error;
		}
	}
	state.isProcessing = false;
};

// 画像・動画投稿開始イベント検知処理
const onStartMediaPostProcess = (): void => {
	state.isMediaPostProcessing = true;
};
// 画像・動画投稿終了イベント検知処理
const onEndMediaPostProcess = (): void => {
	state.isMediaPostProcessing = false;
};

// 投稿ボタンが有効かどうか
const isEnabledPostButton = (): boolean => {
	// 投稿が許可されてない場合は無効
	if (!isPostable) {
		return false;
	}
	// 投稿処理中なら無効
	if (state.isProcessing) {
		return false;
	}
	// 画像・動画投稿中なら無効
	if (state.isMediaPostProcessing) {
		return false;
	}
	return true;
};

const __returned__ = { state, contents, isOverContentsMaxLength, status, apiValidationErrorViewModel, postTalkRoomId, isContentsEmpty, isNickNameEmpty, props, communityTalkStore, isPostable, isFormVisible, isRequiredNickName, onChangeContents, onSucessUploadMedias, onResetMedias, talkRoomController, post, onStartMediaPostProcess, onEndMediaPostProcess, isEnabledPostButton, ApiPostLoadMaster, RequireLogin, PostComplete, TalkRoomPostNoticeMail, AppConst, CustomTextarea, CustomLabel, PostNickName, PostMedia, PostProfilePicture, PostReviewerAttribute }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})